import React, { useEffect, useState } from "react";
import { api, BRAND } from "@/lib/avocado";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Landmark, Loader2, ShieldCheck } from "lucide-react";

export function BankDetailsDialog() {
  const [open, setOpen] = useState(false);
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState({ account_name: "", sort_code: "", account_number: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/bank");
      setExisting(data.has_details ? data : null);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (open) load(); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/me/bank", form);
      toast.success("Bank details saved securely 🔒");
      setForm({ account_name: "", sort_code: "", account_number: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="rounded-full h-11 px-5 font-bold border-2 flex items-center gap-2 bg-white transition-colors hover:bg-muted/40"
          style={{ borderColor: BRAND.pink, color: BRAND.pink }} data-testid="bank-details-button">
          <Landmark size={16} strokeWidth={2.5} />
          {existing ? "Update bank details" : "Add bank details"}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="bank-details-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2"><ShieldCheck size={18} style={{ color: BRAND.pink }} /><p className="uppercase-label" style={{ color: BRAND.pink }}>Encrypted at rest</p></div>
          <DialogTitle className="font-display text-3xl">Bank details</DialogTitle>
          <DialogDescription>Tom uses these to pay your 5% fee when a sale completes. Only Tom can see them.</DialogDescription>
        </DialogHeader>
        {existing && (
          <div className="rounded-xl p-4 border-2 mt-2" style={{ background: "#FFF0F8", borderColor: "#FFAED8" }}>
            <div className="uppercase-label" style={{ color: BRAND.pink }}>On file</div>
            <div className="text-sm mt-1 font-semibold">{existing.account_name}</div>
            <div className="text-xs tabular" style={{ color: "#7A6D5F" }}>Sort {existing.sort_code_masked} · Acct {existing.account_number_masked}</div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="uppercase-label">Account name</Label>
            <Input required value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })}
              placeholder="Jane Smith" className="h-11 rounded-xl" data-testid="bank-account-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="uppercase-label">Sort code</Label>
              <Input required value={form.sort_code} maxLength={8} onChange={(e) => setForm({ ...form, sort_code: e.target.value })}
                placeholder="12-34-56" className="h-11 rounded-xl tabular" data-testid="bank-sort-code" />
            </div>
            <div className="space-y-1.5">
              <Label className="uppercase-label">Account no.</Label>
              <Input required value={form.account_number} maxLength={12} onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                placeholder="12345678" className="h-11 rounded-xl tabular" data-testid="bank-account-number" />
            </div>
          </div>
          <DialogFooter className="mt-2">
            <button type="submit" disabled={busy} className="btn-brand w-full h-11 rounded-full font-bold flex items-center justify-center" data-testid="bank-save-button">
              {busy ? <Loader2 className="animate-spin" size={16} /> : (existing ? "Update details" : "Save details")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
